// Minimal RV1960 seed for the Scripture Reader. The full Bible JSON would be
// loaded async from /scriptures/rv1960.json once we ship the asset. For now
// this stub serves common pastoral passages so the feature can be tested
// end-to-end without waiting for the full corpus.

export interface BibleVerse {
  verse: number;
  text: string;
}

export interface BibleChapter {
  book: string;
  chapter: number;
  verses: BibleVerse[];
}

const SEED: Record<string, Record<number, BibleVerse[]>> = {
  Juan: {
    1: [
      {
        verse: 1,
        text: 'En el principio era el Verbo, y el Verbo era con Dios, y el Verbo era Dios.',
      },
      { verse: 2, text: 'Este era en el principio con Dios.' },
      {
        verse: 3,
        text: 'Todas las cosas por él fueron hechas, y sin él nada de lo que ha sido hecho, fue hecho.',
      },
      {
        verse: 14,
        text: 'Y aquel Verbo fue hecho carne, y habitó entre nosotros (y vimos su gloria, gloria como del unigénito del Padre), lleno de gracia y de verdad.',
      },
    ],
    3: [
      {
        verse: 16,
        text: 'Porque de tal manera amó Dios al mundo, que ha dado a su Hijo unigénito, para que todo aquel que en él cree, no se pierda, mas tenga vida eterna.',
      },
      {
        verse: 17,
        text: 'Porque no envió Dios a su Hijo al mundo para condenar al mundo, sino para que el mundo sea salvo por él.',
      },
    ],
  },
  Romanos: {
    8: [
      {
        verse: 1,
        text: 'Ahora, pues, ninguna condenación hay para los que están en Cristo Jesús, los que no andan conforme a la carne, sino conforme al Espíritu.',
      },
      {
        verse: 28,
        text: 'Y sabemos que a los que aman a Dios, todas las cosas les ayudan a bien, esto es, a los que conforme a su propósito son llamados.',
      },
      {
        verse: 29,
        text: 'Porque a los que antes conoció, también los predestinó para que fuesen hechos conformes a la imagen de su Hijo, para que él sea el primogénito entre muchos hermanos.',
      },
      {
        verse: 30,
        text: 'Y a los que predestinó, a éstos también llamó; y a los que llamó, a éstos también justificó; y a los que justificó, a éstos también glorificó.',
      },
    ],
  },
  '2 Timoteo': {
    2: [
      {
        verse: 2,
        text: 'Lo que has oído de mí ante muchos testigos, esto encarga a hombres fieles que sean idóneos para enseñar también a otros.',
      },
      {
        verse: 15,
        text: 'Procura con diligencia presentarte a Dios aprobado, como obrero que no tiene de qué avergonzarse, que usa bien la palabra de verdad.',
      },
    ],
  },
  Salmos: {
    23: [
      { verse: 1, text: 'Jehová es mi pastor; nada me faltará.' },
      {
        verse: 2,
        text: 'En lugares de delicados pastos me hará descansar; junto a aguas de reposo me pastoreará.',
      },
      {
        verse: 3,
        text: 'Confortará mi alma; me guiará por sendas de justicia por amor de su nombre.',
      },
      {
        verse: 4,
        text: 'Aunque ande en valle de sombra de muerte, no temeré mal alguno, porque tú estarás conmigo; tu vara y tu cayado me infundirán aliento.',
      },
    ],
  },
  Génesis: {
    1: [
      { verse: 1, text: 'En el principio creó Dios los cielos y la tierra.' },
      {
        verse: 2,
        text: 'Y la tierra estaba desordenada y vacía, y las tinieblas estaban sobre la faz del abismo, y el Espíritu de Dios se movía sobre la faz de las aguas.',
      },
    ],
  },
  Hebreos: {
    4: [
      {
        verse: 12,
        text: 'Porque la palabra de Dios es viva y eficaz, y más cortante que toda espada de dos filos; y penetra hasta partir el alma y el espíritu, las coyunturas y los tuétanos, y discierne los pensamientos y las intenciones del corazón.',
      },
    ],
    11: [
      {
        verse: 1,
        text: 'Es, pues, la fe la certeza de lo que se espera, la convicción de lo que no se ve.',
      },
    ],
  },
};

const BOOK_ALIASES: Record<string, string> = {
  Jn: 'Juan',
  Juan: 'Juan',
  Rom: 'Romanos',
  Romanos: 'Romanos',
  '2Tim': '2 Timoteo',
  '2 Tim': '2 Timoteo',
  '2 Timoteo': '2 Timoteo',
  '2Ti': '2 Timoteo',
  Sal: 'Salmos',
  Salmos: 'Salmos',
  Salmo: 'Salmos',
  Gn: 'Génesis',
  Gen: 'Génesis',
  Génesis: 'Génesis',
  Heb: 'Hebreos',
  Hebreos: 'Hebreos',
};

export function canonicalBook(input: string): string | null {
  const key = input.trim();
  return BOOK_ALIASES[key] ?? (SEED[key] ? key : null);
}

export function listAvailableBooks(): string[] {
  return Object.keys(SEED).sort();
}

export function getChapter(book: string, chapter: number): BibleChapter | null {
  const canon = canonicalBook(book);
  if (!canon) return null;
  const verses = SEED[canon]?.[chapter];
  if (!verses) return null;
  return { book: canon, chapter, verses };
}

export interface ScriptureRef {
  book: string;
  chapter: number;
  verses?: string;
}

/** Parse strings like "Juan 3:16", "Rom 8:28-30", "Sal 23". */
export function parseReference(input: string): ScriptureRef | null {
  const m = input.match(/^\s*(\d?\s?[A-Za-zÁÉÍÓÚÑáéíóúñ]+)\s+(\d+)(?::([\d\-,\s]+))?\s*$/);
  if (!m) return null;
  const book = canonicalBook(m[1]!.trim());
  if (!book) return null;
  const ref: ScriptureRef = { book, chapter: parseInt(m[2]!, 10) };
  if (m[3]) ref.verses = m[3].trim();
  return ref;
}

export function formatReference(ref: ScriptureRef): string {
  return `${ref.book} ${ref.chapter}${ref.verses ? `:${ref.verses}` : ''}`;
}
